import { useState, useEffect, useRef } from 'react';
import { Layout } from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, UserCircle, Edit2, Trash2, Copy, Check, Key, Users, Upload, Download, FileSpreadsheet, Eye, Building2, CreditCard, ChevronDown, ChevronUp } from 'lucide-react';
import api from '@/utils/api';
import { toast } from 'sonner';
import { clearAppCache } from '@/utils/cacheUtils';

const RELATION_TYPES = [
  { value: 'father', label: 'Father (पिता)' },
  { value: 'husband', label: 'Husband (पति)' },
  { value: 'mother', label: 'Mother (माता)' },
  { value: 'wife', label: 'Wife (पत्नी)' },
  { value: 'son', label: 'Son (पुत्र)' },
  { value: 'daughter', label: 'Daughter (पुत्री)' },
  { value: 'brother', label: 'Brother (भाई)' },
  { value: 'sister', label: 'Sister (बहन)' },
  { value: 'guardian', label: 'Guardian (अभिभावक)' },
  { value: 'c/o', label: 'C/O (देखभाल)' },
  { value: 'other', label: 'Other (अन्य)' }
];

export default function Customers({ user, onLogout }) {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [credentialsDialogOpen, setCredentialsDialogOpen] = useState(false);
  const [newCustomerCredentials, setNewCustomerCredentials] = useState(null);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [copiedField, setCopiedField] = useState(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewCustomer, setViewCustomer] = useState(null);
  const [customerBookings, setCustomerBookings] = useState([]);
  const [loadingBookings, setLoadingBookings] = useState(false);
  
  const [formData, setFormData] = useState({
    customer_name: '',
    mobile_number: '',
    address: '',
    relation_type: '',
    relation_name: ''
  });

  const [editFormData, setEditFormData] = useState({
    customer_name: '',
    mobile_number: '',
    address: '',
    relation_type: '',
    relation_name: ''
  });

  const [isLoading, setIsLoading] = useState(true);

  const filteredCustomers = searchQuery.trim()
    ? customers.filter(c =>
        (c.customer_name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (c.mobile_number || '').includes(searchQuery) ||
        (c.relation_name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (c.address || '').toLowerCase().includes(searchQuery.toLowerCase())
      )
    : customers;

  const viewCustomerBookings = async (customer) => {
    setViewCustomer(customer);
    setLoadingBookings(true);
    setCustomerBookings([]);
    try {
      const res = await api.get(`/simple-customers/${customer.id}/bookings`);
      setCustomerBookings(res.data || []);
    } catch {
      toast.error('Bookings load nahi ho payi');
    } finally {
      setLoadingBookings(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    setIsLoading(true);
    try {
      const response = await api.get('/simple-customers');
      // Sort by newest first so Smart Import customers appear at top
      const sorted = (response.data || []).sort((a, b) => {
        const da = new Date(a.created_at || 0);
        const db = new Date(b.created_at || 0);
        return db - da;
      });
      setCustomers(sorted);
    } catch (error) {
      toast.error('Failed to load customers');
    } finally {
      setIsLoading(false);
    }
  };

  // Download sample CSV template - Simple format
  const downloadSampleCSV = () => {
    // Simple headers without confusing technical names
    const headers = ['Name (नाम)', 'Mobile (मोबाइल)', 'Address (पता)', 'Relation (संबंध)', 'Relation Name (संबंध का नाम)'];
    const sampleData = [
      ['राजेश कुमार', '9876543210', 'मकान नं 123, सेक्टर 5, दिल्ली', 'S/O', 'श्री रामेश्वर'],
      ['Priya Sharma', '9988776655', 'Flat 201, Green Park, Mumbai', 'W/O', 'Amit Sharma'],
      ['Amit Singh', '8877665544', 'Plot 45, Sector 12, Noida', 'S/O', 'Rajan Singh']
    ];
    
    const csvContent = [headers.join(','), ...sampleData.map(row => row.map(cell => `"${cell}"`).join(','))].join('\n');
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8' }); // BOM for Hindi support
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'customer_sample.csv';
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Sample file downloaded! Edit karke upload karo');
  };

  // Handle bulk CSV upload - Simple format support
  const handleBulkUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const reader = new FileReader();
    
    reader.onload = async (event) => {
      try {
        const text = event.target.result;
        const lines = text.split('\n').filter(line => line.trim());
        
        // Parse CSV properly (handle quoted values)
        const parseCSVLine = (line) => {
          const result = [];
          let current = '';
          let inQuotes = false;
          for (let char of line) {
            if (char === '"') {
              inQuotes = !inQuotes;
            } else if (char === ',' && !inQuotes) {
              result.push(current.trim());
              current = '';
            } else {
              current += char;
            }
          }
          result.push(current.trim());
          return result;
        };
        
        const headerRow = parseCSVLine(lines[0].replace(/\r/g, ''));
        
        // Smart header matching - works with corrupted Hindi, partial matches, etc.
        const matchHeader = (rawHeader) => {
          const h = rawHeader.toLowerCase().replace(/['"]/g, '').trim();
          // Extract just the English part before any parenthesis or special chars
          const englishPart = h.replace(/\s*\(.*$/, '').trim();
          
          // Direct exact match first
          const directMap = {
            'name': 'customer_name', 'customer_name': 'customer_name',
            'mobile': 'mobile_number', 'mobile_number': 'mobile_number', 'phone': 'mobile_number',
            'address': 'address',
            'relation': 'relation_type', 'relation_type': 'relation_type',
            'relation name': 'relation_name', 'relation_name': 'relation_name'
          };
          
          if (directMap[h]) return directMap[h];
          if (directMap[englishPart]) return directMap[englishPart];
          
          // Fuzzy: check if header starts with known keywords
          if (h.startsWith('name') || h.startsWith('customer')) return 'customer_name';
          if (h.startsWith('mobile') || h.startsWith('phone') || h.startsWith('contact')) return 'mobile_number';
          if (h.startsWith('address') || h.startsWith('addr')) return 'address';
          if (h.startsWith('relation name') || h.startsWith('relation_name')) return 'relation_name';
          if (h.startsWith('relation') || h.startsWith('rel')) return 'relation_type';
          
          return null;
        };
        
        const fieldMapping = headerRow.map(h => matchHeader(h));
        
        const customers = [];
        for (let i = 1; i < lines.length; i++) {
          const line = lines[i].replace(/\r/g, '').trim();
          if (!line) continue;
          const values = parseCSVLine(line);
          const customer = {};
          
          fieldMapping.forEach((apiField, index) => {
            if (apiField && values[index]) {
              customer[apiField] = values[index].replace(/['"]/g, '').trim();
            }
          });
          
          if (customer.customer_name && customer.mobile_number) {
            customers.push(customer);
          }
        }

        if (customers.length === 0) {
          toast.error('कोई valid customer नहीं मिला। Name और Mobile ज़रूरी है।');
          return;
        }

        // Use bulk upload API for fast processing
        try {
          const res = await api.post('/simple-customers/bulk-upload', { customers });
          await clearAppCache();
          toast.success(`${res.data.success} customers upload हो गए! ${res.data.failed > 0 ? `(${res.data.failed} failed)` : ''}`);
          setUploadDialogOpen(false);
          fetchCustomers();
        } catch (err) {
          toast.error('Upload failed: ' + (err.response?.data?.detail || 'Unknown error'));
        }
      } catch (error) {
        toast.error('File parse करने में error। सही format use करें।');
      } finally {
        setUploading(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    };

    reader.readAsText(file);
  };

  // Download all customers as CSV
  const downloadCustomersCSV = () => {
    const headers = ['Customer Name', 'Mobile', 'Address', 'Relation Type', 'Relation Name', 'Added On'];
    const rows = customers.map(c => [
      c.customer_name,
      c.mobile_number,
      c.address || '',
      c.relation_type || '',
      c.relation_name || '',
      c.created_at ? new Date(c.created_at).toLocaleDateString('en-IN') : ''
    ]);

    const csvContent = [headers.join(','), ...rows.map(row => row.map(cell => `"${cell}"`).join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `customers_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Customers CSV downloaded!');
  };

  const handleSubmit = async (e, forceCreate = false) => {
    if (e) e.preventDefault();
    setLoading(true);
    try {
      const url = forceCreate ? '/simple-customers?force=true' : '/simple-customers';
      const response = await api.post(url, formData);
      
      // Check if it's a duplicate warning
      if (response.data.duplicate_warning) {
        const confirmed = window.confirm(
          `⚠️ ${response.data.message}\n\nExisting: ${response.data.existing_customer}\n\nPhir bhi naya customer banana hai toh OK dabayein.`
        );
        if (confirmed) {
          await handleSubmit(null, true);
        } else {
          setLoading(false);
        }
        return;
      }
      
      await clearAppCache();
      
      // Show credentials dialog
      setNewCustomerCredentials({
        name: response.data.customer_name,
        login_id: response.data.login_id,
        password: response.data.password
      });
      setCredentialsDialogOpen(true);
      
      setDialogOpen(false);
      setFormData({ customer_name: '', mobile_number: '', address: '', relation_type: '', relation_name: '' });
      fetchCustomers();
    } catch (error) {
      const msg = error?.response?.data?.detail || 'Failed to add customer';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async (text, field) => {
    await navigator.clipboard.writeText(text);
    setCopiedField(field);
    toast.success('Copied!');
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleEdit = (customer) => {
    setEditingCustomer(customer);
    setEditFormData({
      customer_name: customer.customer_name,
      mobile_number: customer.mobile_number,
      address: customer.address,
      relation_type: customer.relation_type || '',
      relation_name: customer.relation_name || ''
    });
    setEditDialogOpen(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.put(`/simple-customers/${editingCustomer.id}`, editFormData);
      await clearAppCache();
      toast.success('Customer updated!');
      setEditDialogOpen(false);
      setEditingCustomer(null);
      fetchCustomers();
    } catch (error) {
      toast.error('Failed to update customer');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (customerId) => {
    if (!window.confirm('Are you sure you want to delete this customer?')) return;
    
    try {
      await api.delete(`/simple-customers/${customerId}`);
      await clearAppCache();
      toast.success('Customer deleted!');
      setSelectedIds(prev => { const n = new Set(prev); n.delete(customerId); return n; });
      fetchCustomers();
    } catch (error) {
      toast.error('Failed to delete customer');
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === customers.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(customers.map(c => c.id)));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Kya aap ${selectedIds.size} customers delete karna chahte hain? Yeh action undo nahi hoga!`)) return;
    setBulkDeleting(true);
    try {
      await api.post('/simple-customers/bulk-delete', { ids: Array.from(selectedIds) });
      await clearAppCache();
      toast.success(`${selectedIds.size} customers deleted!`);
      setSelectedIds(new Set());
      fetchCustomers();
    } catch (error) {
      toast.error('Bulk delete failed');
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleMergeDuplicates = async () => {
    if (!window.confirm('Duplicate customers ko merge karein?\n\nSirf SAME mobile + SAME name wale customers merge honge.\nAlag naam wale (family members) safe rahenge.\nBookings automatically update ho jayengi.\n\nProceed?')) return;
    setLoading(true);
    try {
      const res = await api.post('/simple-customers/dedup');
      await clearAppCache();
      if (res.data.duplicates_removed > 0) {
        toast.success(`${res.data.duplicates_removed} duplicate customers merge kiye! ${res.data.bookings_updated} bookings updated.`);
      } else {
        toast.success('Koi duplicate nahi mila - sab clean hai!');
      }
      fetchCustomers();
    } catch (error) {
      toast.error('Merge failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRepairBookings = async () => {
    if (!window.confirm('Booking assignments repair karein?\n\nJin bookings ka customer name linked customer se match nahi karta,\nunhe sahi customer ko reassign karega.\nAgar customer delete ho gaya tha toh naya banayega.\n\nProceed?')) return;
    setLoading(true);
    try {
      const res = await api.post('/simple-customers/repair-bookings');
      await clearAppCache();
      if (res.data.bookings_fixed > 0) {
        toast.success(`${res.data.bookings_fixed} bookings sahi customer ko assign ki! ${res.data.customers_recreated} customers recreated.`);
      } else {
        toast.success('Sab bookings sahi assigned hain!');
      }
      fetchCustomers();
    } catch (error) {
      toast.error('Repair failed');
    } finally {
      setLoading(false);
    }
  };

  const [healthReport, setHealthReport] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);

  const runHealthCheck = async () => {
    setHealthLoading(true);
    try {
      const res = await api.get('/system/health-check');
      setHealthReport(res.data);
      if (res.data.healthy) {
        toast.success(res.data.message);
      } else {
        toast.error(res.data.message);
      }
    } catch (error) {
      toast.error('Health check failed');
    } finally {
      setHealthLoading(false);
    }
  };

  const handleFixDuplicateBookings = async () => {
    if (!window.confirm('Duplicate booking numbers fix karein?\n\nSame booking number wali entries mein newer entry ko naya unique number milega.\n\nProceed?')) return;
    setLoading(true);
    try {
      const res = await api.post('/bookings/fix-duplicate-numbers');
      if (res.data.fixed > 0) {
        toast.success(`${res.data.fixed} duplicate booking numbers fix kiye!`);
      } else {
        toast.success('Koi duplicate booking number nahi mila!');
      }
    } catch (error) {
      toast.error('Fix failed');
    } finally {
      setLoading(false);
    }
  };

  const showCredentials = (customer) => {
    setNewCustomerCredentials({
      name: customer.customer_name,
      login_id: customer.login_id,
      password: customer.password
    });
    setCredentialsDialogOpen(true);
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6 max-w-6xl mx-auto">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-3">
              <CardTitle>Customer Management</CardTitle>
              <div className="flex items-center gap-2 flex-wrap">
                {/* Search */}
                <Input
                  placeholder="Search name, mobile, address..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="w-64"
                  data-testid="customer-search-input"
                />
                {/* Bulk Upload Button */}
                <Button variant="outline" onClick={handleMergeDuplicates} className="gap-2 text-amber-700 border-amber-300 hover:bg-amber-50" data-testid="merge-duplicates-btn">
                  <Users size={16} />
                  Merge Duplicates
                </Button>
                <Button variant="outline" onClick={handleRepairBookings} className="gap-2 text-blue-700 border-blue-300 hover:bg-blue-50" data-testid="repair-bookings-btn">
                  <CreditCard size={16} />
                  Repair Bookings
                </Button>
                <Button variant="outline" onClick={runHealthCheck} disabled={healthLoading} className="gap-2 text-emerald-700 border-emerald-300 hover:bg-emerald-50" data-testid="health-check-btn">
                  <Eye size={16} />
                  {healthLoading ? 'Checking...' : 'Health Check'}
                </Button>
                <Button variant="outline" onClick={handleFixDuplicateBookings} className="gap-2 text-rose-700 border-rose-300 hover:bg-rose-50" data-testid="fix-dup-bookings-btn">
                  <CreditCard size={16} />
                  Fix Dup Bookings
                </Button>
                <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" className="gap-2">
                      <Upload size={16} />
                      Bulk Upload
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Customers Bulk Upload</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                        <p className="text-sm text-emerald-800 font-medium mb-2">
                          CSV Format - 5 columns:
                        </p>
                        <div className="text-xs text-emerald-700 space-y-1">
                          <p>• <strong>Name (नाम)</strong> - Customer का नाम</p>
                          <p>• <strong>Mobile (मोबाइल)</strong> - 10 digit number</p>
                          <p>• <strong>Address (पता)</strong> - पूरा पता</p>
                          <p>• <strong>Relation (संबंध)</strong> - S/O, W/O, D/O, C/O</p>
                          <p>• <strong>Relation Name (संबंध का नाम)</strong> - पिता/पति का नाम</p>
                        </div>
                        <Button variant="outline" size="sm" onClick={downloadSampleCSV} className="gap-2 mt-3 bg-white">
                          <FileSpreadsheet size={14} />
                          Sample File Download करें
                        </Button>
                      </div>
                      <div className="space-y-2">
                        <Label>CSV File Select करें</Label>
                        <Input
                          ref={fileInputRef}
                          type="file"
                          accept=".csv"
                          onChange={handleBulkUpload}
                          disabled={uploading}
                        />
                        <p className="text-xs text-slate-500">Excel में बनाकर .csv में save करें</p>
                      </div>
                      {uploading && (
                        <div className="flex items-center gap-2 text-emerald-600">
                          <div className="w-4 h-4 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin"></div>
                          Uploading customers...
                        </div>
                      )}
                    </div>
                  </DialogContent>
                </Dialog>

                {/* Download Customers */}
                <Button variant="outline" onClick={downloadCustomersCSV} className="gap-2">
                  <Download size={16} />
                  Export CSV
                </Button>

                {/* Add Customer */}
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="bg-emerald-600 hover:bg-emerald-700 gap-2">
                      <Plus size={18} />
                      Add Customer
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add New Customer</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Customer Name *</Label>
                      <Input
                        placeholder="Enter customer name"
                        value={formData.customer_name}
                        onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Mobile Number *</Label>
                      <Input
                        placeholder="10-digit mobile number"
                        value={formData.mobile_number}
                        onChange={(e) => setFormData({ ...formData, mobile_number: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Address *</Label>
                      <textarea
                        rows={3}
                        placeholder="Enter full address"
                        value={formData.address}
                        onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                        required
                        className="w-full px-3 py-2 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>
                    
                    {/* Guardian/Relation Section */}
                    <div className="pt-3 border-t border-slate-200">
                      <Label className="flex items-center gap-2 text-slate-700 mb-3">
                        <Users size={14} /> Guardian / Relative Details
                      </Label>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                          <Label className="text-sm">Relation</Label>
                          <Select value={formData.relation_type} onValueChange={(v) => setFormData({ ...formData, relation_type: v })}>
                            <SelectTrigger><SelectValue placeholder="Select relation" /></SelectTrigger>
                            <SelectContent>
                              {RELATION_TYPES.map(r => (
                                <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm">Name</Label>
                          <Input
                            placeholder="Father/Husband/etc. name"
                            value={formData.relation_name}
                            onChange={(e) => setFormData({ ...formData, relation_name: e.target.value })}
                          />
                        </div>
                      </div>
                    </div>
                    
                    <Button type="submit" disabled={loading} className="w-full bg-emerald-600 hover:bg-emerald-700">
                      {loading ? 'Adding...' : 'Add Customer'}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
              </div>
            </div>
          </CardHeader>
        </Card>

        {/* Health Check Report */}
        {healthReport && (
          <Card className={healthReport.healthy ? 'border-emerald-300 bg-emerald-50/50' : 'border-red-300 bg-red-50/50'}>
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className={`font-bold text-sm ${healthReport.healthy ? 'text-emerald-700' : 'text-red-700'}`}>
                  {healthReport.healthy ? '✅ System Healthy' : '⚠️ Issues Found'}
                </h3>
                <button onClick={() => setHealthReport(null)} className="text-xs text-slate-400 hover:text-slate-600">Close</button>
              </div>
              <div className="grid grid-cols-4 gap-3 mb-3">
                <div className="text-center p-2 bg-white rounded-lg border"><p className="text-lg font-bold text-slate-800">{healthReport.summary?.total_customers}</p><p className="text-xs text-slate-500">Customers</p></div>
                <div className="text-center p-2 bg-white rounded-lg border"><p className="text-lg font-bold text-slate-800">{healthReport.summary?.total_bookings}</p><p className="text-xs text-slate-500">Bookings</p></div>
                <div className="text-center p-2 bg-white rounded-lg border"><p className="text-lg font-bold text-slate-800">{healthReport.summary?.total_leads}</p><p className="text-xs text-slate-500">Leads</p></div>
                <div className="text-center p-2 bg-white rounded-lg border"><p className="text-lg font-bold text-slate-800">{healthReport.summary?.total_properties}</p><p className="text-xs text-slate-500">Properties</p></div>
              </div>
              {healthReport.critical_issues?.length > 0 && (
                <div className="space-y-2 mb-2">
                  {healthReport.critical_issues.map((issue, i) => (
                    <div key={i} className="p-2 bg-red-100 rounded-lg border border-red-200 text-xs text-red-800">
                      <strong>{issue.type}</strong>: {issue.count} issues {issue.message && `- ${issue.message}`}
                      {issue.details?.slice(0, 3).map((d, j) => <div key={j} className="ml-2 mt-1 text-red-600">{JSON.stringify(d)}</div>)}
                    </div>
                  ))}
                </div>
              )}
              {healthReport.warnings?.length > 0 && (
                <div className="space-y-2">
                  {healthReport.warnings.map((w, i) => (
                    <div key={i} className="p-2 bg-amber-100 rounded-lg border border-amber-200 text-xs text-amber-800">
                      <strong>{w.type}</strong>: {w.count} {w.message && `- ${w.message}`}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        <Card>
          <CardContent className="pt-6">
            {/* Bulk Delete Toolbar */}
            {user?.role === 'admin' && selectedIds.size > 0 && (
              <div className="flex items-center justify-between mb-4 p-3 bg-red-50 border border-red-200 rounded-xl" data-testid="bulk-delete-toolbar">
                <span className="text-sm font-medium text-red-700">
                  {selectedIds.size} selected
                </span>
                <Button
                  size="sm"
                  variant="destructive"
                  data-testid="bulk-delete-btn"
                  onClick={handleBulkDelete}
                  disabled={bulkDeleting}
                  className="gap-2"
                >
                  <Trash2 size={14} />
                  {bulkDeleting ? 'Deleting...' : `Delete ${selectedIds.size} Selected`}
                </Button>
              </div>
            )}
            <Table>
              <TableHeader>
                <TableRow>
                  {user?.role === 'admin' && (
                    <TableHead className="w-10">
                      <input
                        type="checkbox"
                        data-testid="select-all-customers"
                        checked={filteredCustomers.length > 0 && selectedIds.size === filteredCustomers.length}
                        onChange={toggleSelectAll}
                        className="rounded border-slate-300 accent-emerald-600 w-4 h-4"
                      />
                    </TableHead>
                  )}
                  <TableHead>Customer Name</TableHead>
                  <TableHead>Guardian/Relation</TableHead>
                  <TableHead>Mobile Number</TableHead>
                  <TableHead>Login Credentials</TableHead>
                  <TableHead>Added On</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={user?.role === 'admin' ? 7 : 6} className="text-center py-8">
                      <div className="flex items-center justify-center gap-2 text-slate-500">
                        <div className="w-5 h-5 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin"></div>
                        Loading customers...
                      </div>
                    </TableCell>
                  </TableRow>
                ) : filteredCustomers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={user?.role === 'admin' ? 7 : 6} className="text-center py-8 text-slate-500">
                      {searchQuery ? `"${searchQuery}" se koi customer nahi mila` : 'No customers found. Add your first customer!'}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredCustomers.map((customer) => (
                    <TableRow key={customer.id} className={`hover:bg-slate-50 ${selectedIds.has(customer.id) ? 'bg-emerald-50' : ''}`}>
                      {user?.role === 'admin' && (
                        <TableCell>
                          <input
                            type="checkbox"
                            data-testid={`select-customer-${customer.id}`}
                            checked={selectedIds.has(customer.id)}
                            onChange={() => toggleSelect(customer.id)}
                            className="rounded border-slate-300 accent-emerald-600 w-4 h-4"
                          />
                        </TableCell>
                      )}
                      <TableCell className="font-semibold flex items-center gap-2">
                        <UserCircle size={16} className="text-emerald-600" />
                        {customer.customer_name}
                      </TableCell>
                      <TableCell>
                        {customer.relation_type && customer.relation_name ? (
                          <div>
                            <span className="text-xs text-slate-500 capitalize">{customer.relation_type}: </span>
                            <span className="font-medium">{customer.relation_name}</span>
                          </div>
                        ) : (
                          <span className="text-slate-400 text-sm">-</span>
                        )}
                      </TableCell>
                      <TableCell>{customer.mobile_number}</TableCell>
                      <TableCell>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => showCredentials(customer)}
                          className="gap-2 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                        >
                          <Key size={14} />
                          View Credentials
                        </Button>
                      </TableCell>
                      <TableCell className="text-sm text-slate-500">
                        {new Date(customer.created_at).toLocaleDateString('en-IN')}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => viewCustomerBookings(customer)}
                            className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 gap-1"
                            data-testid={`view-bookings-${customer.id}`}
                          >
                            <Building2 size={14} />
                            Bookings
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            onClick={() => handleEdit(customer)}
                            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          >
                            <Edit2 size={14} />
                          </Button>
                          {user?.role === 'admin' && (
                          <Button 
                            size="sm" 
                            variant="outline" 
                            onClick={() => handleDelete(customer.id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 size={14} />
                          </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Edit Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Customer</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleUpdate} className="space-y-4">
              <div className="space-y-2">
                <Label>Customer Name *</Label>
                <Input
                  placeholder="Enter customer name"
                  value={editFormData.customer_name}
                  onChange={(e) => setEditFormData({ ...editFormData, customer_name: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Mobile Number *</Label>
                <Input
                  placeholder="10-digit mobile number"
                  value={editFormData.mobile_number}
                  onChange={(e) => setEditFormData({ ...editFormData, mobile_number: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Address *</Label>
                <textarea
                  rows={3}
                  placeholder="Enter full address"
                  value={editFormData.address}
                  onChange={(e) => setEditFormData({ ...editFormData, address: e.target.value })}
                  required
                  className="w-full px-3 py-2 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              
              {/* Guardian/Relation Section */}
              <div className="pt-3 border-t border-slate-200">
                <Label className="flex items-center gap-2 text-slate-700 mb-3">
                  <Users size={14} /> Guardian / Relative Details
                </Label>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className="text-sm">Relation</Label>
                    <Select value={editFormData.relation_type} onValueChange={(v) => setEditFormData({ ...editFormData, relation_type: v })}>
                      <SelectTrigger><SelectValue placeholder="Select relation" /></SelectTrigger>
                      <SelectContent>
                        {RELATION_TYPES.map(r => (
                          <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm">Name</Label>
                    <Input
                      placeholder="Father/Husband/etc. name"
                      value={editFormData.relation_name}
                      onChange={(e) => setEditFormData({ ...editFormData, relation_name: e.target.value })}
                    />
                  </div>
                </div>
              </div>
              
              <Button type="submit" disabled={loading} className="w-full bg-emerald-600 hover:bg-emerald-700">
                {loading ? 'Updating...' : 'Update Customer'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* View Bookings Dialog */}
        <Dialog open={!!viewCustomer} onOpenChange={(open) => { if (!open) { setViewCustomer(null); setCustomerBookings([]); } }}>
          <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2" data-testid="bookings-dialog-title">
                <Building2 className="text-emerald-600" size={20} />
                {viewCustomer?.customer_name} - Bookings
              </DialogTitle>
              <p className="text-sm text-slate-500">Customer ki saari bookings aur payment status</p>
            </DialogHeader>
            {loadingBookings ? (
              <div className="flex items-center justify-center py-12 gap-2 text-slate-500" data-testid="bookings-loading">
                <div className="w-5 h-5 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin"></div>
                Loading bookings...
              </div>
            ) : customerBookings.length === 0 ? (
              <div className="text-center py-12 text-slate-500" data-testid="no-bookings-msg">
                <Building2 size={40} className="mx-auto mb-3 text-slate-300" />
                <p className="font-medium">Koi booking nahi mili</p>
                <p className="text-sm mt-1">Is customer ke naam ya mobile number se koi billing record nahi hai.</p>
              </div>
            ) : (
              <div className="space-y-4" data-testid="bookings-list">
                <div className="flex items-center gap-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                  <div className="text-sm text-emerald-800">
                    <span className="font-semibold">{customerBookings.length}</span> booking(s) found for <span className="font-semibold">{viewCustomer?.customer_name}</span> ({viewCustomer?.mobile_number})
                  </div>
                </div>
                {customerBookings.map((booking, idx) => {
                  const statusBadge = booking.payment_status === 'Paid'
                    ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
                    : booking.payment_status === 'Partially Paid'
                    ? 'bg-amber-100 text-amber-700 border-amber-200'
                    : 'bg-red-100 text-red-700 border-red-200';
                  return (
                    <div key={booking.id || idx} className="border border-slate-200 rounded-lg overflow-hidden" data-testid={`booking-card-${idx}`}>
                      {/* Booking Header */}
                      <div className="bg-slate-50 px-4 py-3 flex items-center justify-between flex-wrap gap-2">
                        <div>
                          <p className="font-semibold text-slate-800">{booking.property_name || 'N/A'}</p>
                          <p className="text-xs text-slate-500">
                            {booking.booking_number && `#${booking.booking_number}`}
                            {booking.property_size && ` | ${booking.property_size}`}
                            {booking.payment_type && ` | ${booking.payment_type}`}
                          </p>
                        </div>
                        <span className={`px-3 py-1 text-xs font-bold rounded-full border ${statusBadge}`} data-testid={`booking-status-${idx}`}>
                          {booking.payment_status || 'Pending'}
                        </span>
                      </div>
                      {/* Price Summary */}
                      <div className="grid grid-cols-3 gap-3 px-4 py-3 border-b border-slate-100">
                        <div>
                          <p className="text-xs text-slate-500">Total Price</p>
                          <p className="font-bold text-slate-800">Rs. {(booking.total_price || 0).toLocaleString('en-IN')}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">Paid Amount</p>
                          <p className="font-bold text-emerald-600">Rs. {(booking.total_paid || 0).toLocaleString('en-IN')}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">Balance Due</p>
                          <p className={`font-bold ${(booking.balance_due || 0) > 0 ? 'text-red-600' : 'text-slate-500'}`}>
                            Rs. {(booking.balance_due || 0).toLocaleString('en-IN')}
                          </p>
                        </div>
                      </div>
                      {/* Installments */}
                      {booking.installments && booking.installments.length > 0 && (
                        <div className="px-4 py-3">
                          <p className="text-xs font-semibold text-slate-600 mb-2 flex items-center gap-1">
                            <CreditCard size={12} /> Installments ({booking.installments.length})
                          </p>
                          <div className="space-y-1.5 max-h-40 overflow-y-auto">
                            {booking.installments.map((inst, iIdx) => {
                              const instStatus = inst.status || 'Pending';
                              const instDot = instStatus === 'Paid' ? 'bg-emerald-500' : instStatus === 'Partially Paid' ? 'bg-amber-500' : 'bg-slate-400';
                              const instBadge = instStatus === 'Paid' ? 'bg-emerald-100 text-emerald-700' : instStatus === 'Partially Paid' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600';
                              return (
                                <div key={iIdx} className="flex items-center justify-between text-sm py-1.5 px-2 rounded bg-slate-50">
                                  <div className="flex items-center gap-2">
                                    <span className={`w-2 h-2 rounded-full ${instDot}`}></span>
                                    <span className="text-slate-700">{inst.installment_name || inst.label || `Installment ${iIdx + 1}`}</span>
                                    {inst.due_date && <span className="text-xs text-slate-400">({new Date(inst.due_date).toLocaleDateString('en-IN')})</span>}
                                  </div>
                                  <div className="flex items-center gap-3">
                                    <span className="text-slate-600">Rs. {(inst.amount || 0).toLocaleString('en-IN')}</span>
                                    <span className={`text-xs font-semibold px-2 py-0.5 rounded ${instBadge}`}>
                                      {instStatus}
                                    </span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Credentials Dialog */}
        <Dialog open={credentialsDialogOpen} onOpenChange={setCredentialsDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Key className="text-emerald-600" size={20} />
                Customer Login Credentials
              </DialogTitle>
            </DialogHeader>
            {newCustomerCredentials && (
              <div className="space-y-4">
                <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-200">
                  <p className="text-sm text-emerald-700 font-medium mb-1">Customer Name</p>
                  <p className="text-lg font-bold text-slate-800">{newCustomerCredentials.name}</p>
                </div>
                
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs text-slate-500 uppercase">Login ID</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <code className="flex-1 p-3 bg-slate-100 rounded-lg text-lg font-mono font-bold text-slate-800">
                        {newCustomerCredentials.login_id}
                      </code>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleCopy(newCustomerCredentials.login_id, 'login_id')}
                        className="shrink-0"
                      >
                        {copiedField === 'login_id' ? <Check size={16} className="text-green-600" /> : <Copy size={16} />}
                      </Button>
                    </div>
                  </div>
                  
                  <div>
                    <Label className="text-xs text-slate-500 uppercase">Password</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <code className="flex-1 p-3 bg-amber-50 rounded-lg text-lg font-mono font-bold text-amber-800 border border-amber-200">
                        {newCustomerCredentials.password}
                      </code>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleCopy(newCustomerCredentials.password, 'password')}
                        className="shrink-0"
                      >
                        {copiedField === 'password' ? <Check size={16} className="text-green-600" /> : <Copy size={16} />}
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-700">
                    <strong>Customer Portal URL:</strong>
                    <br />
                    <code className="text-xs">{window.location.origin}/customer-login</code>
                  </p>
                </div>

                <Button 
                  onClick={() => {
                    const text = `Ganesa Homestate - Customer Login\n\nLogin ID: ${newCustomerCredentials.login_id}\nPassword: ${newCustomerCredentials.password}\n\nPortal: ${window.location.origin}/customer-login`;
                    handleCopy(text, 'all');
                  }}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 gap-2"
                >
                  <Copy size={16} />
                  Copy All Details
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
